/* =====================================================================
 * MessageBubble — 개별 메시지 (사용자/어시스턴트)
 *
 * 그룹채팅일 경우 다른 팀원 메시지를 왼쪽에 배치하며
 * 아바타, 이름, 시간, 이모지 리액션을 지원합니다.
 * ===================================================================== */

import React from 'react';
import styled, { css, keyframes } from 'styled-components';
import {
  Copy,
  Check,
  ThumbsUp,
  ThumbsDown,
  AlertTriangle,
  FileText,
  FileSpreadsheet,
  Image as ImageIcon,
  File as FileIcon,
  FileVideo,
  FileAudio,
  Download,
  Eye,
  Loader,
  Play,
  X as XIcon,
  ZoomIn,
  ChevronLeft,
  ChevronRight,
  Smile,
  Plus,
  RefreshCw,
  ChevronDown,
} from 'lucide-react';
import type { ChatMessage, OutputFileInfo, Reaction, ConversationSharedGroup } from '@auw/workspace-utils';
import { Avatar } from '@auw/workspace-ui';
import { DotsSpinner } from '@auw/workspace-ui';
import { Tooltip } from '@auw/workspace-ui';
import { EmojiPicker } from '@auw/workspace-ui';
import { useChatStore } from '@auw/stores';
import { canPreviewFile, isVideoFile, isAudioFile } from '@auw/workspace-ui';
import { StreamProgress } from '../StreamProgress';
import { ThinkingBlock } from '../ThinkingBlock';
import { downloadAgentFile, previewAgentFile } from '@auw/api-client';
import { getFileDownloadUrl, getFileServeUrl, getFileSignedUrl } from '@auw/api-client';
import { authFetch } from '@auw/api-client';
import { useChatFeature } from '../ChatModeContext';
import DMInvitationCard from '../../dm/DMInvitationCard';
import { useTranslation } from 'react-i18next';

const MarkdownRenderer = React.lazy(() =>
  import('../MarkdownRenderer').then((m) => ({ default: m.MarkdownRenderer })),
);

const WidgetRenderer = React.lazy(() => import('../widget/WidgetRenderer'));

const PREVIEW_FAILED_SENTINEL = 'failed';
const PREVIEW_DELETED_SENTINEL = 'deleted';
const PREVIEW_TOO_LARGE_SENTINEL = 'too_large';
const OUTPUT_IMAGE_FRAME_SIZE = 320;
const OUTPUT_VIDEO_FRAME_WIDTH = 360;

function isPreviewFailure(value: string | undefined): boolean {
  return value === PREVIEW_FAILED_SENTINEL || value === PREVIEW_DELETED_SENTINEL || value === PREVIEW_TOO_LARGE_SENTINEL;
}

function isRenderablePreviewUrl(value: string | undefined): value is string {
  return !!value && !isPreviewFailure(value);
}

function getPreviewFailureMessage(value: string | undefined, t: (key: string) => string): string {
  if (value === PREVIEW_DELETED_SENTINEL) return t('chat.deletedFile');
  if (value === PREVIEW_TOO_LARGE_SENTINEL) return '파일 용량이 200MB를 초과합니다.';
  return t('chat.previewFailed');
}

function resolvePreviewFailureSentinel(error: unknown): string {
  if (error instanceof Error) {
    if (error.message === PREVIEW_DELETED_SENTINEL) return PREVIEW_DELETED_SENTINEL;
    if (error.message === PREVIEW_TOO_LARGE_SENTINEL) return PREVIEW_TOO_LARGE_SENTINEL;
  }
  return PREVIEW_FAILED_SENTINEL;
}

/* ------------------------------------------------------------------ */
/* Clipboard fallback                                                  */
/* ------------------------------------------------------------------ */

async function copyTextFallback(text: string): Promise<boolean> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch { /* fallthrough */ }
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.cssText = 'position:fixed;left:-9999px;top:-9999px;opacity:0;';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Row = styled.div<{ $role: string; $group?: boolean; $narrow?: boolean }>`
  display: flex;
  gap: ${({ theme }) => theme.bubble.rowGap};
  max-width: ${({ theme }) => theme.sizes.maxBubbleWidth};
  width: 100%;
  margin: 0 auto;
  padding: ${({ theme }) => theme.bubble.rowPad};
  animation: ${fadeIn} 0.3s ease;

  ${({ $role }) =>
    $role === 'user' &&
    css`
      justify-content: flex-end;
    `}

  ${({ $role, $group, $narrow, theme }) =>
    $role === 'assistant' && !$group && !$narrow &&
    css`
      /* 풀폭 1:1 에이전트 채팅 전용: 좌측 로고 아바타(32px) + rowGap 만큼 답변이
       * 우측으로 밀리므로 그 절반만큼 좌측으로 당겨 입력바와 시각적 중심을 맞춘다
       * (아바타는 좌측 거터로 빠짐). 레이아웃 박스/가로 스크롤 영향 없음. 32px = Avatar 기본 size.
       *
       * 주의: transform: translateX 가 아니라 개별 translate 속성을 쓴다.
       * 이 Row 는 진입 시 fadeIn(transform: translateY 8px->0) 애니메이션을 받는데,
       * 정적 오프셋도 transform 으로 주면 같은 속성이 충돌 -> 애니메이션 동안 translateY 가
       * 정적 translateX 를 덮었다가 0.3s 종료 순간 정적값으로 스냅하며 "왼쪽으로 한 번 점프".
       * translate(개별 속성)은 transform 과 별개로 합성되므로 오프셋이 0프레임부터 고정 ->
       * 점프 없이 fadeIn 슬라이드업과 공존한다.
       * 제외 대상:
       *  - $group: chip + ReplyConnector(꼬리표)가 같은 정렬선을 공유 → 시프트 시 어긋남.
       *  - $narrow: 사이드 미리보기로 칼럼이 좁아진 상태 → 시프트 시 아바타가 칼럼 밖
       *    (미리보기 영역)으로 밀려 가려짐. 좁은 칼럼에선 입력바도 칼럼폭 기준이라 시프트 불필요. */
      translate: calc((32px + ${theme.bubble.rowGap}) / -2) 0;
    `}

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: ${({ theme }) => theme.bubbleMobile.rowGap};
    padding: ${({ theme }) => theme.bubbleMobile.rowPad};
    /* 모바일은 단일 칼럼 풀폭이라 데스크톱용 좌측 시프트(translate)를
     * 그대로 두면 어시스턴트 로고 아바타가 화면 좌측 밖으로 밀려 잘린다.
     * 시프트를 0 으로 되돌려 아바타가 항상 온전히 보이게 한다. */
    translate: 0 0;
  }
`;

const SystemRow = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 8px 16px;
`;

const SystemStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  width: 100%;
`;

const SystemBubble = styled.div<{ $plain?: boolean }>`
  max-width: ${({ theme }) => theme.sizes.maxMessageWidth};
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.8rem;
  line-height: 1.4;

  ${({ $plain, theme }) => ($plain
    ? css`
      padding: 0;
      background: transparent;
      border: none;
      border-radius: 0;
    `
    : css`
      padding: 6px 12px;
      background: ${theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'};
      border: 1px solid ${theme.colors.border};
      border-radius: 999px;
    `)}
`;

const SystemActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 120px;
  height: 32px;
  padding: 0 14px;
  border: none;
  border-radius: 999px;
  background: #111111;
  color: #ffffff;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.58;
    cursor: not-allowed;
  }
`;

const AvatarCol = styled.div`
  margin-top: 2px;
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

/* 통합 에이전트(agent) · DM 등 모든 모드에서 라이브 에이전트(`LiveAgentPage`
 * 의 `UserBubble`) 와 동일한 말풍선 형태(`16px 16px 4px 16px`, 우하단을
 * 살짝 깎은 꼬리 모양) 를 사용한다.  과거에는 그룹채팅(`$tail=true`) 일
 * 때만 꼬리 모양을, 그 외에는 전체 둥근 사각형(`borderRadiusLg`) 을 썼지만
 * UI 일관성을 위해 단일 형태로 통일했다.  `$tail` prop 은 더 이상 의미가
 * 없어 시그니처에서 제거됐다. */
const UserBubble = styled.div<{ $hasCollapsedToggle?: boolean }>`
  position: relative;
  background: ${({ theme }) => theme.colors.bgUserMessage};
  border-radius: 16px 16px 4px 16px;
  padding: ${({ theme }) => theme.bubble.userPad};
  ${({ $hasCollapsedToggle }) => $hasCollapsedToggle && 'padding-bottom: 34px;'}
  width: fit-content;
  cursor: pointer;
  transition: opacity ${({ theme }) => theme.transitions.fast};

  &:hover {
    opacity: 0.85;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.bubbleMobile.userPad};
    ${({ $hasCollapsedToggle }) => $hasCollapsedToggle && 'padding-bottom: 30px;'}
  }
`;

const UserContent = styled.div`
  font-size: ${({ theme }) => theme.bubble.userFontSize};
  line-height: ${({ theme }) => theme.bubble.userLineHeight};
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: pre-wrap;
  word-break: break-word;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.bubbleMobile.userFontSize};
    line-height: ${({ theme }) => theme.bubbleMobile.userLineHeight};
  }
`;

const UserContentToggle = styled.button<{ $align: 'left' | 'right' }>`
  position: absolute;
  bottom: 8px;
  ${({ $align }) => ($align === 'left' ? 'left: 10px;' : 'right: 10px;')}
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(255,255,255,0.75)'};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 999px;
  padding: 2px 8px;
  cursor: pointer;
  z-index: 2;

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(0,0,0,0.58)' : 'rgba(255,255,255,0.9)'};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    bottom: 6px;
    font-size: 0.6875rem;
  }
`;

const UserSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  max-width: ${({ theme }) => theme.bubble.userMaxWidth};
`;

const UserActionsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 2px;
  margin-top: 4px;
  animation: ${fadeIn} 0.2s ease;
`;

const AssistantContent = styled.div`
  font-size: ${({ theme }) => theme.bubble.assistantFontSize};
  line-height: ${({ theme }) => theme.bubble.assistantLineHeight};
  color: ${({ theme }) => theme.colors.textPrimary};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.bubbleMobile.assistantFontSize};
    line-height: ${({ theme }) => theme.bubbleMobile.assistantLineHeight};
  }
`;

const AssistantPlainTextFallback = styled.div`
  white-space: pre-wrap;
  word-break: break-word;
`;

const AttachmentList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const BubbleFileCard = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 10px;
  padding: 8px 12px;
  min-width: 160px;
  max-width: 220px;
`;

const BubbleIconBadge = styled.div<{ $bg: string; $color: string }>`
  width: ${({ theme }) => theme.bubble.fileIconSize};
  height: ${({ theme }) => theme.bubble.fileIconSize};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  flex-shrink: 0;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: ${({ theme }) => theme.bubbleMobile.fileIconSize};
    height: ${({ theme }) => theme.bubbleMobile.fileIconSize};
  }

  /* 아이콘 SVG가 축소되지 않도록 보호 */
  svg {
    flex-shrink: 0;
  }
`;

const BubbleExtLabel = styled.span<{ $color: string }>`
  font-size: ${({ theme }) => theme.bubble.fileExtFontSize};
  font-weight: 700;
  letter-spacing: 0.02em;
  color: ${({ $color }) => $color};
  line-height: 1;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.bubbleMobile.fileExtFontSize};
  }
`;

const BubbleFileInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const BubbleFileName = styled.span`
  font-size: ${({ theme }) => theme.bubble.fileFontSize};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.bubbleMobile.fileFontSize};
  }
`;

const BubbleFileSize = styled.span`
  font-size: ${({ theme }) => theme.bubble.fileSizeFontSize};
  color: ${({ theme }) => theme.colors.textMuted};
  line-height: 1.2;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.bubbleMobile.fileSizeFontSize};
  }
`;

const attachmentPreviewSkeleton = keyframes`
  0%   { background-position: -480px 0; }
  100% { background-position:  480px 0; }
`;

const BubbleImageThumbFrame = styled.div`
  position: relative;
  width: ${({ theme }) => theme.bubble.fileIconSize};
  height: ${({ theme }) => theme.bubble.fileIconSize};
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.bgSecondary};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: ${({ theme }) => theme.bubbleMobile.fileIconSize};
    height: ${({ theme }) => theme.bubbleMobile.fileIconSize};
  }
`;

const BubbleImageThumb = styled.img<{ $visible?: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;

  ${({ $visible }) =>
    !$visible &&
    css`
      visibility: hidden;
      pointer-events: none;
    `}
`;

const BubbleImageThumbSkeleton = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.bgHover} 25%,
    ${({ theme }) => theme.colors.bgSecondary} 50%,
    ${({ theme }) => theme.colors.bgHover} 75%
  );
  background-size: 480px 100%;
  animation: ${attachmentPreviewSkeleton} 1.4s ease-in-out infinite;
`;

const BubbleImageThumbPlaceholder = styled.div<{ $nonInteractive?: boolean }>`
  position: absolute;
  inset: 0;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textMuted};
  background: ${({ theme }) => theme.colors.bgSecondary};
  font-size: 0.64rem;
  line-height: 1.2;
  text-align: center;
  cursor: ${({ $nonInteractive }) => ($nonInteractive ? 'default' : 'inherit')};
  user-select: none;
`;

const USER_BUBBLE_COLLAPSE_CHARS = 500;

const BubbleDownloadBtn = styled.button`
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textPrimary};
  background: ${({ theme }) => theme.colors.bgSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  transition: all ${({ theme }) => theme.transitions.fast};
  flex-shrink: 0;
  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: ${({ theme }) => theme.bubble.errorPad};
  background: ${({ theme }) => theme.colors.errorBg};
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.bubble.errorFontSize};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  margin-top: 8px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.bubbleMobile.errorPad};
    font-size: ${({ theme }) => theme.bubbleMobile.errorFontSize};
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 2px;
  margin-top: 6px;
`;

const ActionBtn = styled.button`
  padding: ${({ theme }) => theme.bubble.actionBtnPad};
  border-radius: 6px;
  color: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.85)' : 'rgba(58, 58, 58, 0.86)'};
  transition: all ${({ theme }) => theme.transitions.fast};
  display: flex;
  align-items: center;
  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.bubbleMobile.actionBtnPad};
  }
`;

const thumbShake = keyframes`
  0%   { transform: rotate(0deg); }
  20%  { transform: rotate(-18deg); }
  40%  { transform: rotate(14deg); }
  60%  { transform: rotate(-8deg); }
  80%  { transform: rotate(4deg); }
  100% { transform: rotate(0deg); }
`;

const FeedbackBtn = styled.button<{ $active: boolean }>`
  padding: ${({ theme }) => theme.bubble.actionBtnPad};
  border-radius: 6px;
  color: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.85)' : 'rgba(58, 58, 58, 0.86)'};
  transition: color ${({ theme }) => theme.transitions.fast}, background ${({ theme }) => theme.transitions.fast};
  display: flex;
  align-items: center;
  & > svg {
    fill: ${({ $active }) => ($active ? 'currentColor' : 'none')};
    transition: fill 0.15s ease;
  }
  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
  &.shaking > svg {
    animation: ${thumbShake} 0.45s ease-in-out;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.bubbleMobile.actionBtnPad};
  }
`;

const RegenerateGroup = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  margin-left: 2px;
`;

const RegenerateBtn = styled.button`
  padding: ${({ theme }) => theme.bubble.actionBtnPad};
  border-radius: 6px;
  color: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.85)' : 'rgba(58, 58, 58, 0.86)'};
  transition: all ${({ theme }) => theme.transitions.fast};
  display: flex;
  align-items: center;
  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.bubbleMobile.actionBtnPad};
  }
`;

const RegenerateModeBtn = styled.button`
  padding: 3px 6px;
  margin-left: 4px;
  border-radius: 6px;
  color: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.65)' : 'rgba(58, 58, 58, 0.65)'};
  transition: all ${({ theme }) => theme.transitions.fast};
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 13px;
  font-weight: 500;
  line-height: 1;
  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const RegenerateModePopup = styled.div`
  position: absolute;
  bottom: calc(100% + 4px);
  right: 0;
  background: ${({ theme }) => theme.colors.bgSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  padding: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 10;
  min-width: 96px;
`;

const RegenerateModeOption = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.textPrimary : theme.colors.textSecondary};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.bgHover : 'transparent'};
  transition: background ${({ theme }) => theme.transitions.fast};
  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
  }
`;

const ChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  column-gap: 8px;
  align-items: flex-start;
  padding-top: 4px;
  margin-bottom: 4px;
`;

const MetaRow = styled.div`
  display: flex;
  gap: 12px;
  font-size: ${({ theme }) => theme.bubble.metaFontSize};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-top: 4px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.bubbleMobile.metaFontSize};
  }
`;

const OutputFileList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  /* 파일 카드 위(텍스트)·아래(액션 아이콘) 사이 여백 확보. */
  margin-top: 12px;
  margin-bottom: 4px;
`;

const OutputImageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 8px;
`;

const AssistantWallpaperCard = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  margin-top: 2px;
  padding: 14px 16px 12px;
  border-radius: 22px;
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.52)'};
  /* 글래스 컨테이너 채움 불투명도를 높여 뒤 배경(월페이퍼)이 덜 비치게 한다.
   * blur 는 유지해 글래스 질감은 보존. */
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(18,18,22,0.55)' : 'rgba(255,255,255,0.58)'};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 16px 36px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.06)'
      : '0 14px 32px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.38)'};
  backdrop-filter: blur(20px) saturate(155%);
  -webkit-backdrop-filter: blur(20px) saturate(155%);

  ${ChipsRow} {
    padding-top: 0;
    margin-bottom: 8px;
  }

  ${AssistantContent},
  ${OutputImageList},
  ${ErrorBanner},
  ${MetaRow},
  ${Actions} {
    margin-top: 0;
  }

  /* 출력 파일 카드는 글래스 카드 안에서도 위(텍스트)·아래(액션 아이콘)
   * 사이에 여백을 둔다(다른 자식들의 margin-top:0 리셋에서 제외). */
  ${OutputFileList} {
    margin-top: 12px;
    margin-bottom: 12px;
  }
`;

const OutputImageBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const OutputImageFrame = styled.div`
  position: relative;
  width: min(${OUTPUT_IMAGE_FRAME_SIZE}px, calc(100vw - 112px));
  max-width: 100%;
  aspect-ratio: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.bgSecondary};
`;

const OutputImageOverlay = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textPrimary};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.85)'};
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  transition: all ${({ theme }) => theme.transitions.fast};
  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(0,0,0,0.65)' : 'rgba(255,255,255,0.95)'};
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const OverlaySpin = styled.span`
  display: inline-flex;
  animation: ${keyframes`
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  `} 1s linear infinite;
`;

const OutputFileCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 10px;
  padding: 10px 12px;
  min-width: 220px;
  max-width: 420px;
`;

const OutputFileMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const OutputFileHint = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  line-height: 1.2;
`;

const OutputFileActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
`;

const OutputFileAction = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 8px;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  background: ${({ theme }) => theme.colors.bgSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  transition: all ${({ theme }) => theme.transitions.fast};
  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const OutputImagePreview = styled.img`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
  flex-shrink: 0;
`;

const OutputVideoFrame = styled(OutputImageFrame)`
  width: min(${OUTPUT_VIDEO_FRAME_WIDTH}px, calc(100vw - 112px));
  aspect-ratio: 16 / 9;
  background:
    linear-gradient(180deg, rgba(15, 23, 42, 0.08), rgba(15, 23, 42, 0.18)),
    ${({ theme }) => theme.colors.bgSecondary};
`;

const OutputVideoPreview = styled.video`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
  background: #000000;
`;

const OutputAudioCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 12px;
  padding: 14px;
  min-width: 280px;
  max-width: 420px;
`;

const OutputAudioPlayer = styled.audio`
  width: 100%;
  height: 40px;
  border-radius: 8px;
  outline: none;

  &::-webkit-media-controls-panel {
    border-radius: 8px;
  }
`;

const OutputMediaActionBar = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const OutputMediaActionButton = styled(OutputImageOverlay)`
  position: static;
`;

const OutputVideoPreviewTrigger = styled.button`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;

  &:hover::after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.12);
    pointer-events: none;
  }
`;

const OutputVideoPlayBadge = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 56px;
  height: 56px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.96);
  background: rgba(15, 23, 42, 0.72);
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.3);
  pointer-events: none;
`;

const OutputImagePlaceholder = styled.div<{ $nonInteractive?: boolean }>`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  text-align: center;
  padding: 0 16px;
  cursor: ${({ $nonInteractive }) => ($nonInteractive ? 'default' : 'inherit')};
  user-select: none;
`;

const imageSkeleton = keyframes`
  0%   { background-position: -480px 0; }
  100% { background-position:  480px 0; }
`;

const OutputImageSkeleton = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.bgHover}     25%,
    ${({ theme }) => theme.colors.bgSecondary} 50%,
    ${({ theme }) => theme.colors.bgHover}     75%
  );
  background-size: 480px 100%;
  animation: ${imageSkeleton} 1.4s ease-in-out infinite;
`;

/* Lightbox overlay */
const lightboxFadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const LightboxOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.88);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${lightboxFadeIn} 0.2s ease;
  cursor: zoom-out;
`;

const LightboxImg = styled.img`
  max-width: 90vw;
  max-height: 90vh;
  border-radius: 10px;
  object-fit: contain;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.5);
  cursor: default;
  user-select: none;
`;

const LightboxClose = styled.button`
  position: fixed;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  transition: background 0.15s;
  &:hover {
    background: rgba(255, 255, 255, 0.22);
  }
`;

const LightboxNav = styled.button<{ $side: 'left' | 'right' }>`
  position: fixed;
  top: 50%;
  ${({ $side }) => $side}: 20px;
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  transition: background 0.15s;
  &:hover {
    background: rgba(255, 255, 255, 0.22);
  }
`;

const LightboxCounter = styled.div`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.65);
  letter-spacing: 0.05em;
`;

const ImageClickable = styled.div`
  cursor: zoom-in;
  display: flex;
  position: relative;
  width: 100%;
  height: 100%;
  &:hover::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.12);
    pointer-events: none;
  }
`;

/* ---- Group Chat Styles ---- */

const GroupRow = styled.div`
  display: flex;
  gap: 8px;
  max-width: ${({ theme }) => theme.sizes.maxBubbleWidth};
  width: 100%;
  margin: 0 auto;
  padding: ${({ theme }) => theme.bubble.rowPad};
  animation: ${fadeIn} 0.3s ease;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: 6px;
    padding: ${({ theme }) => theme.bubbleMobile.rowPad};
  }
`;

const GroupAvatarCol = styled.div`
  flex-shrink: 0;
  margin-top: 2px;
`;

const GroupMemberAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? '#3a3a3a' : '#e8e5e1'};
  color: ${({ theme }) =>
    theme.mode === 'dark' ? '#d4d0cc' : '#5c5550'};
  font-weight: 700;
  font-size: 0.8125rem;
  user-select: none;
  flex-shrink: 0;
  overflow: hidden;
`;

const GroupMemberPicture = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
`;

const GroupBubbleWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-width: 75%;
  min-width: 0;
  position: relative;
`;

const GroupSenderRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  padding: 0 2px;
`;

const GroupSenderName = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#ffffff' : '#000000')};
`;

const GroupTimestamp = styled.span`
  font-size: 0.6875rem;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#ffffff' : '#000000')};
`;

const GroupBubble = styled.div<{ $hasCollapsedToggle?: boolean }>`
  position: relative;
  background: ${({ theme }) => theme.colors.bgSecondary};
  color: ${({ theme }) => theme.colors.textPrimary};
  border-radius: 4px 16px 16px 16px;
  padding: ${({ theme }) => theme.bubble.userPad};
  ${({ $hasCollapsedToggle }) => $hasCollapsedToggle && 'padding-bottom: 34px;'}
  width: fit-content;
  cursor: pointer;
  transition: opacity ${({ theme }) => theme.transitions.fast};
  &:hover {
    opacity: 0.85;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.bubbleMobile.userPad};
    ${({ $hasCollapsedToggle }) => $hasCollapsedToggle && 'padding-bottom: 30px;'}
  }
`;

const GroupBubbleContent = styled.div`
  font-size: ${({ theme }) => theme.bubble.userFontSize};
  line-height: ${({ theme }) => theme.bubble.userLineHeight};
  white-space: pre-wrap;
  word-break: break-word;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.bubbleMobile.userFontSize};
    line-height: ${({ theme }) => theme.bubbleMobile.userLineHeight};
  }
`;

const MentionInlineChip = styled.span`
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  padding: 0 6px;
  margin: 0 1px;
  border-radius: 6px;
  font-size: 0.86em;
  font-weight: 600;
  line-height: 1.35;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#7dd3fc' : '#1264a3')};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(56,189,248,0.18)' : 'rgba(18,100,163,0.14)'};
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const GroupActionsRow = styled.div`
  display: flex;
  gap: 2px;
  margin-top: 4px;
  animation: ${slideUp} 0.12s ease;
`;

/* ---- Reply indicator (Discord-style) ---- */

const ReplyRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0;
  /* 그룹채팅 어시스턴트 버블(Row, maxBubbleWidth)과 폭을 맞춰 chip/꼬리표를 정렬한다.
   * Row·ReplyRow 둘 다 그룹챗에서는 시프트가 없으므로(아래 Row transform 은 !$group)
   * 같은 폭·같은 중앙정렬로 꼬리표와 버블이 어긋나지 않는다. */
  max-width: ${({ theme }) => theme.sizes.maxBubbleWidth};
  width: 100%;
  margin: 0 auto;
  padding: ${({ theme }) => theme.bubble.rowPad};
  padding-top: 0;
  padding-bottom: 0;
  margin-bottom: -10px;
  cursor: pointer;
  overflow: visible;
`;

const ReplyConnector = styled.div`
  position: relative;
  width: 24px;
  height: 14px;
  margin-left: 15px;
  margin-right: 6px;
  flex-shrink: 0;
  border-left: 2px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.13)'};
  border-top: 2px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.13)'};
  border-top-left-radius: 6px;

  &::after {
    content: '';
    position: absolute;
    left: -2px;
    bottom: -10px;
    width: 2px;
    height: 10px;
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.13)'};
  }
`;

const ReplyChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 12px;
  margin-top: 6px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(18,18,22,0.42)' : 'rgba(255,255,255,0.44)'};
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.52)'};
  backdrop-filter: blur(20px) saturate(155%);
  -webkit-backdrop-filter: blur(20px) saturate(155%);
  max-width: calc(100% - 60px);
  transition: background 0.15s ease;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(18,18,22,0.52)' : 'rgba(255,255,255,0.54)'};
  }
`;

const ReplyMiniAvatar = styled.img`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

const ReplyMiniAvatarFallback = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(251,146,60,0.25)' : 'rgba(251,146,60,0.20)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 600;
  color: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(251,146,60,0.9)' : 'rgb(194,97,12)'};
  flex-shrink: 0;
`;

const ReplyName = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) =>
    theme.mode === 'dark' ? '#ffffff' : 'rgb(194,97,12)'};
  white-space: nowrap;
  line-height: 1;
`;

const ReplyContent = styled.span`
  font-size: 12px;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#ffffff' : theme.colors.textMuted)};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 220px;
  display: inline-block;
  vertical-align: middle;
  line-height: 1;
`;

const ReactionBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 3px 0 1px;
`;

const ReactionBadge = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 4px 8px;
  border-radius: 10px;
  border: none;
  background: ${({ $active, theme }) =>
    $active
      ? theme.mode === 'dark'
        ? 'rgba(99,102,241,0.18)'
        : 'rgba(99,102,241,0.10)'
      : theme.mode === 'dark'
        ? 'rgba(255,255,255,0.06)'
        : 'rgba(0,0,0,0.04)'};
  cursor: pointer;
  font-size: 13px;
  line-height: 1;
  transition: all 0.15s ease;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark'
        ? 'rgba(99,102,241,0.22)'
        : 'rgba(99,102,241,0.12)'};
  }
`;

const ReactionAddBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border-radius: 10px;
  border: 1px dashed ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)'};
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(99,102,241,0.4)' : 'rgba(99,102,241,0.3)'};
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(99,102,241,0.10)' : 'rgba(99,102,241,0.06)'};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const ReactionCount = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-weight: 500;
`;

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

/* ---- File type helpers ---- */

interface FileTypeInfo {
  icon: React.ReactNode;
  color: string;
  bg: string;
  ext: string;
}

/* MIME type → 확장자 매핑 (파일명에 확장자가 없을 때 폴백) */
const MIME_TO_EXT: Record<string, string> = {
  'application/pdf': 'pdf',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'text/csv': 'csv',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/x-hwp': 'hwp',
  'application/hwp+zip': 'hwpx',
  'application/vnd.ms-powerpoint': 'ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
  'text/plain': 'txt',
  'text/markdown': 'md',
  'video/mp4': 'mp4',
  'video/webm': 'webm',
  'video/quicktime': 'mov',
};

function getBubbleFileType(name: string, type: string): FileTypeInfo {
  const parts = name.split('.');
  // 파일명에 '.'이 있어야 진짜 확장자 — 없으면 빈 문자열
  let ext = parts.length > 1 ? (parts.pop()?.toLowerCase() || '') : '';
  // 확장자가 너무 길면(10자 초과) 실제 확장자가 아님
  if (ext.length > 10) ext = '';
  // 확장자를 못 찾았으면 MIME type으로 폴백
  if (!ext && type) {
    ext = MIME_TO_EXT[type.split(';')[0].trim().toLowerCase()] || '';
  }

  if (type.startsWith('image/'))
    return { icon: <ImageIcon size={16} />, color: '#8B5CF6', bg: 'rgba(139,92,246,0.1)', ext: ext.toUpperCase() || 'IMG' };

  if (type.startsWith('video/'))
    return { icon: <FileVideo size={16} />, color: '#7C3AED', bg: 'rgba(124,58,237,0.12)', ext: ext.toUpperCase() || 'VIDEO' };

  if (type.startsWith('audio/'))
    return { icon: <FileAudio size={16} />, color: '#F59E0B', bg: 'rgba(245,158,11,0.1)', ext: ext.toUpperCase() || 'AUDIO' };

  switch (ext) {
    case 'pdf':
      return { icon: <FileText size={16} />, color: '#EF4444', bg: 'rgba(239,68,68,0.1)', ext: 'PDF' };
    case 'xls': case 'xlsx':
      return { icon: <FileSpreadsheet size={16} />, color: '#22C55E', bg: 'rgba(34,197,94,0.1)', ext: ext.toUpperCase() };
    case 'csv':
      return { icon: <FileSpreadsheet size={16} />, color: '#22C55E', bg: 'rgba(34,197,94,0.1)', ext: 'CSV' };
    case 'doc': case 'docx':
      return { icon: <FileText size={16} />, color: '#3B82F6', bg: 'rgba(59,130,246,0.1)', ext: ext.toUpperCase() };
    case 'hwp': case 'hwpx':
      return { icon: <FileText size={16} />, color: '#0F766E', bg: 'rgba(15,118,110,0.12)', ext: ext.toUpperCase() };
    case 'ppt': case 'pptx':
      return { icon: <FileText size={16} />, color: '#F97316', bg: 'rgba(249,115,22,0.1)', ext: ext.toUpperCase() };
    case 'txt': case 'md':
      return { icon: <FileText size={16} />, color: '#6B7280', bg: 'rgba(107,114,128,0.1)', ext: ext.toUpperCase() };
    case 'mp4': case 'webm': case 'mov': case 'm4v': case 'ogv':
      return { icon: <FileVideo size={16} />, color: '#7C3AED', bg: 'rgba(124,58,237,0.12)', ext: ext.toUpperCase() };
    case 'mp3': case 'wav': case 'ogg': case 'flac': case 'm4a': case 'aac':
      return { icon: <FileAudio size={16} />, color: '#F59E0B', bg: 'rgba(245,158,11,0.1)', ext: ext.toUpperCase() };
    default:
      return { icon: <FileIcon size={16} />, color: '#6B7280', bg: 'rgba(107,114,128,0.1)', ext: ext.toUpperCase() || 'FILE' };
  }
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isMentionStartBoundary(ch: string | undefined): boolean {
  if (!ch) return true;
  return /[\s([{"'“‘]/.test(ch);
}

function isMentionEndBoundary(ch: string | undefined): boolean {
  if (!ch) return true;
  return /[\s)\]}",.!?:;'“”‘’…]/.test(ch);
}

function buildMentionCandidates(sharedGroup?: ConversationSharedGroup | null): string[] {
  const set = new Set<string>();
  set.add('askyour.ai');
  (sharedGroup?.members ?? []).forEach((m) => {
    const name = typeof m.name === 'string' ? m.name.trim() : '';
    const emailPrefix =
      typeof m.email === 'string' && m.email.includes('@')
        ? m.email.split('@')[0].trim()
        : '';
    if (name) set.add(name);
    if (emailPrefix) set.add(emailPrefix);
  });
  return Array.from(set).sort((a, b) => b.length - a.length);
}

type MentionPart = { type: 'text' | 'mention'; value: string };

function splitMentionParts(text: string, candidates: string[]): MentionPart[] {
  if (!text) return [];
  const out: MentionPart[] = [];
  let cursor = 0;
  let i = 0;

  const pushText = (end: number) => {
    if (end > cursor) {
      out.push({ type: 'text', value: text.slice(cursor, end) });
      cursor = end;
    }
  };

  while (i < text.length) {
    if (text[i] !== '@' || !isMentionStartBoundary(text[i - 1])) {
      i += 1;
      continue;
    }

    let matched: string | null = null;
    for (const candidate of candidates) {
      const seg = text.slice(i + 1, i + 1 + candidate.length);
      if (
        seg.length === candidate.length
        && seg.toLowerCase() === candidate.toLowerCase()
        && isMentionEndBoundary(text[i + 1 + candidate.length])
      ) {
        matched = candidate;
        break;
      }
    }

    if (matched) {
      pushText(i);
      const mentionValue = `@${text.slice(i + 1, i + 1 + matched.length)}`;
      out.push({ type: 'mention', value: mentionValue });
      i += mentionValue.length;
      cursor = i;
      continue;
    }

    let j = i + 1;
    while (j < text.length) {
      const ch = text[j];
      if (isMentionEndBoundary(ch) || ch === '@') break;
      j += 1;
    }
    if (j > i + 1) {
      pushText(i);
      out.push({ type: 'mention', value: text.slice(i, j) });
      i = j;
      cursor = i;
      continue;
    }

    i += 1;
  }

  if (cursor < text.length) {
    out.push({ type: 'text', value: text.slice(cursor) });
  }
  return out;
}

function renderMentionChips(
  text: string,
  sharedGroup?: ConversationSharedGroup | null,
): React.ReactNode {
  if (!text.includes('@')) return text;
  const parts = splitMentionParts(text, buildMentionCandidates(sharedGroup));
  if (parts.length === 0) return text;
  return parts.map((part, idx) =>
    part.type === 'mention'
      ? <MentionInlineChip key={`m-${idx}`}>{part.value}</MentionInlineChip>
      : <React.Fragment key={`t-${idx}`}>{part.value}</React.Fragment>,
  );
}

function getOutputFileType(name: string, mimeType?: string | null): FileTypeInfo {
  return getBubbleFileType(name, mimeType || '');
}

function isVideoOutputFile(file: Pick<OutputFileInfo, 'name' | 'mime_type'>): boolean {
  const mimeType = String(file.mime_type || '').split(';')[0].trim().toLowerCase();
  return mimeType.startsWith('video/') || isVideoFile(file.name);
}

function isAudioOutputFile(file: Pick<OutputFileInfo, 'name' | 'mime_type' | 'kind'>): boolean {
  if (file.kind === 'audio') return true;
  const mimeType = String(file.mime_type || '').split(';')[0].trim().toLowerCase();
  return mimeType.startsWith('audio/') || isAudioFile(file.name);
}

function canPreviewOutputFile(file: Pick<OutputFileInfo, 'name' | 'mime_type'>): boolean {
  const mimeType = String(file.mime_type || '').split(';')[0].trim().toLowerCase();
  if (mimeType.startsWith('image/') || mimeType.startsWith('video/')) return true;
  return canPreviewFile(file.name);
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export interface ReplyToInfo {
  messageId: string;
  senderName: string;
  senderPicture?: string | null;
  content: string;
}

interface Props {
  message: ChatMessage;
  /** 그룹채팅 정보 — 있으면 그룹채팅 UI 적용 */
  sharedGroup?: ConversationSharedGroup | null;
  /** 현재 유저 ID (그룹채팅에서 내 메시지 판별용) */
  currentUserId?: string;
  /** 대화 ID (이모지 리액션에 필요) */
  conversationId?: string;
  /** 이모지 리액션 토글 콜백 */
  onToggleReaction?: (conversationId: string, messageId: string, emoji: string, userId: string) => void;
  /** 이 메시지가 응답하는 원본 메시지 정보 (그룹채팅 에이전트 답변용) */
  replyTo?: ReplyToInfo | null;
  /** 특정 메시지로 스크롤 이동 */
  onScrollToMessage?: (messageId: string) => void;
  /** 시스템 멤버 재초대 */
  onReinviteMember?: (message: ChatMessage) => void;
  /** 현재 메시지에 재초대 버튼 표시 여부 */
  showReinviteAction?: boolean;
  /** 현재 메시지 재초대 진행 중 여부 */
  reinviting?: boolean;
  /** 답변 재생성 콜백 (어시스턴트 메시지 전용) */
  onRegenerate?: (messageId: string, forceMode: 'fast' | 'plan' | 'skill' | null) => void;
  /**
   * 출력 파일 "열기" 핸들러(선택). 제공되면 렌더 가능한(이미지 제외) 출력
   * 파일 카드의 "열기" 버튼이 이 콜백으로 좌측 렌더를 위임한다. 미제공이면
   * 통합 에이전트(`activeMode==='agent'`)에서 기본 `setPreviewFile` 경로로
   * 좌측 미리보기를 연다. 견적 에이전트처럼 자체 좌측 렌더를 가진 surface 가
   * 주입한다.
   */
  onOpenOutputFile?: (file: OutputFileInfo) => void;
  /** 사이드 미리보기 등으로 채팅 칼럼이 좁을 때 true. 어시스턴트 버블의 좌측 시프트를
   * 꺼서 로고 아바타가 칼럼 밖(미리보기 영역)으로 밀려 가려지는 것을 막는다. */
  narrowColumn?: boolean;
}

function isSameReplyToInfo(a: ReplyToInfo | null | undefined, b: ReplyToInfo | null | undefined): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.messageId === b.messageId
    && a.senderName === b.senderName
    && (a.senderPicture ?? null) === (b.senderPicture ?? null)
    && a.content === b.content
  );
}

function areMessageBubblePropsEqual(prev: Props, next: Props): boolean {
  return (
    prev.message === next.message
    && prev.sharedGroup === next.sharedGroup
    && prev.currentUserId === next.currentUserId
    && prev.conversationId === next.conversationId
    && prev.onToggleReaction === next.onToggleReaction
    && prev.onScrollToMessage === next.onScrollToMessage
    && prev.onReinviteMember === next.onReinviteMember
    && prev.showReinviteAction === next.showReinviteAction
    && prev.reinviting === next.reinviting
    && prev.onRegenerate === next.onRegenerate
    && prev.onOpenOutputFile === next.onOpenOutputFile
    && prev.narrowColumn === next.narrowColumn
    && isSameReplyToInfo(prev.replyTo, next.replyTo)
  );
}

const MessageBubbleImpl: React.FC<Props> = ({
  message,
  sharedGroup,
  currentUserId,
  conversationId,
  onToggleReaction,
  replyTo,
  onScrollToMessage,
  onReinviteMember,
  showReinviteAction = false,
  reinviting = false,
  onRegenerate,
  onOpenOutputFile,
  narrowColumn,
}) => {
  const { t } = useTranslation('common');
  const [copied, setCopied] = React.useState(false);
  const [feedback, setFeedback] = React.useState<'up' | 'down' | null>(null);
  const [shakingFeedback, setShakingFeedback] = React.useState<'up' | 'down' | null>(null);
  const [showUserActions, setShowUserActions] = React.useState(false);
  const [isUserContentExpanded, setIsUserContentExpanded] = React.useState(false);
  const [downloadingId, setDownloadingId] = React.useState<string | null>(null);
  const [openingPreviewId, setOpeningPreviewId] = React.useState<string | null>(null);
  const [attachmentPreviewUrls, setAttachmentPreviewUrls] = React.useState<Record<string, string>>({});
  const [attachmentPreviewLoading, setAttachmentPreviewLoading] = React.useState<Record<string, boolean>>({});
  const [attachmentPreviewStatus, setAttachmentPreviewStatus] = React.useState<
    Record<string, { src: string; status: 'loading' | 'loaded' | 'failed' }>
  >({});
  const [previewUrls, setPreviewUrls] = React.useState<Record<string, string>>({});
  const [previewLoading, setPreviewLoading] = React.useState<Record<string, boolean>>({});
  const [lightboxIdx, setLightboxIdx] = React.useState<number | null>(null);
  const [showGroupActions, setShowGroupActions] = React.useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = React.useState(false);
  const previewUrlsRef = React.useRef<Record<string, string>>({});
  const attachmentPreviewUrlsRef = React.useRef<Record<string, string>>({});
  const latestUserAttachmentsRef = React.useRef<NonNullable<ChatMessage['attachments']>>(message.attachments ?? []);
  const releasedUserPreviewUrlsRef = React.useRef<Set<string>>(new Set());
  const emojiAnchorEl = React.useRef<HTMLElement | null>(null);
  const groupWrapRef = React.useRef<HTMLDivElement>(null);
  const [groupMemberAvatarErrorIds, setGroupMemberAvatarErrorIds] = React.useState<Record<string, boolean>>({});
  const [replyAvatarErrorIds, setReplyAvatarErrorIds] = React.useState<Record<string, boolean>>({});
  const activeMode = useChatStore((s) => s.activeMode);
  const setPreviewFile = useChatStore((s) => s.setPreviewFile);
  const addToast = useChatStore((s) => s.addToast);

  const handleBlockedPreviewClick = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const releaseUserPreviewUrl = React.useCallback((previewUrl?: string) => {
    if (!previewUrl?.startsWith('blob:')) return;
    if (releasedUserPreviewUrlsRef.current.has(previewUrl)) return;
    URL.revokeObjectURL(previewUrl);
    releasedUserPreviewUrlsRef.current.add(previewUrl);
  }, []);

  const handleCopy = async () => {
    const ok = await copyTextFallback(message.content);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleFeedback = React.useCallback((type: 'up' | 'down') => {
    setFeedback((prev) => (prev === type ? null : type));
    setShakingFeedback(type);
    setTimeout(() => setShakingFeedback(null), 450);
  }, []);

  const [regenerateMode, setRegenerateMode] = React.useState<'auto' | 'fast' | 'plan' | 'skill'>('auto');
  const [regenerateMenuOpen, setRegenerateMenuOpen] = React.useState(false);
  const regenerateMenuRef = React.useRef<HTMLDivElement>(null);

  const handleRegenerate = React.useCallback(() => {
    if (onRegenerate) {
      onRegenerate(message.id, regenerateMode === 'auto' ? null : regenerateMode);
    }
    setRegenerateMenuOpen(false);
  }, [onRegenerate, message.id, regenerateMode]);

  React.useEffect(() => {
    if (!regenerateMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (regenerateMenuRef.current && !regenerateMenuRef.current.contains(e.target as Node)) {
        setRegenerateMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [regenerateMenuOpen]);

  const handleDownload = async (file: OutputFileInfo) => {
    try {
      setDownloadingId(file.id);

      if (file.file_id) {
        const signed = await getFileSignedUrl(file.file_id, 'download');
        if (signed.url) {
          const a = document.createElement('a');
          a.href = signed.url;
          a.download = file.name || 'download';
          a.rel = 'noopener';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          return;
        }
        const url = getFileDownloadUrl(file.file_id);
        const res = await authFetch(url);
        if (!res.ok) throw new Error(`Download failed (${res.status})`);
        const blob = await res.blob();
        if (!blob || blob.size === 0) throw new Error('Empty response');
        const blobUrl = URL.createObjectURL(blob);
        const a2 = document.createElement('a');
        a2.href = blobUrl;
        a2.download = file.name || 'download';
        document.body.appendChild(a2);
        a2.click();
        document.body.removeChild(a2);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      } else {
        const sessionId = message.completedData?.session_id;
        const userId = message.completedData?.user_id;
        const messageId = message.completedData?.message_id;
        if (!sessionId || !userId || !messageId) return;
        const blob = await downloadAgentFile({ userId, sessionId, messageId, fileId: file.id });
        if (!blob || blob.size === 0) throw new Error('Empty response');
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = file.name || 'download';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      }
    } catch {
      addToast?.({ type: 'error', title: '파일 다운로드에 실패했습니다.' });
    } finally {
      setDownloadingId(null);
    }
  };

  const openRemoteOutputFilePreview = React.useCallback((file: OutputFileInfo) => {
    const mimeType = file.mime_type || 'application/octet-stream';
    const fileObj =
      typeof File !== 'undefined'
        ? new File([], file.name, { type: mimeType })
        : ({ name: file.name, size: 0, type: mimeType } as unknown as File);

    setPreviewFile({
      id: file.file_id || file.id,
      name: file.name,
      type: mimeType,
      size: file.size,
      file: fileObj,
      remoteUrl: file.file_id ? getFileServeUrl(file.file_id) : undefined,
    });
  }, [setPreviewFile]);

  const handlePreview = React.useCallback(async (file: OutputFileInfo) => {
    if (!canPreviewOutputFile(file)) return;

    if (file.file_id) {
      openRemoteOutputFilePreview(file);
      return;
    }

    const sessionId = message.completedData?.session_id;
    const userId = message.completedData?.user_id;
    const messageId = message.completedData?.message_id;
    if (!sessionId || !userId || !messageId) return;

    try {
      setOpeningPreviewId(file.id);
      const blob = await downloadAgentFile({ userId, sessionId, messageId, fileId: file.id });
      const mimeType = file.mime_type || blob.type || 'application/octet-stream';
      const previewFile =
        typeof File !== 'undefined'
          ? new File([blob], file.name, { type: mimeType })
          : ({ name: file.name, size: blob.size, type: mimeType } as unknown as File);

      setPreviewFile({
        id: file.id,
        name: file.name,
        type: mimeType,
        size: file.size,
        file: previewFile,
      });
    } finally {
      setOpeningPreviewId(null);
    }
  }, [message.completedData?.message_id, message.completedData?.session_id, message.completedData?.user_id, openRemoteOutputFilePreview, setPreviewFile]);

  const handleAttachmentDownload = async (attachment: { id: string; name: string; file?: File; remoteUrl?: string }) => {
    const loadingKey = `att:${attachment.id}`;
    try {
      setDownloadingId(loadingKey);
      if (attachment.remoteUrl || (attachment.file?.size ?? 0) === 0) {
        const signed = await getFileSignedUrl(attachment.id, 'download');
        if (signed.url) {
          const a = document.createElement('a');
          a.href = signed.url;
          a.download = attachment.name || 'download';
          a.rel = 'noopener';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          return;
        }
        const url = getFileDownloadUrl(attachment.id);
        const res = await authFetch(url);
        if (!res.ok) throw new Error(`Download failed (${res.status})`);
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        const a2 = document.createElement('a');
        a2.href = blobUrl;
        a2.download = attachment.name || 'download';
        document.body.appendChild(a2);
        a2.click();
        document.body.removeChild(a2);
        URL.revokeObjectURL(blobUrl);
        return;
      }
      if (!attachment.file) return;
      const blobUrl = URL.createObjectURL(attachment.file);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = attachment.name || 'download';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } finally {
      setDownloadingId(null);
    }
  };

  const isSystem = message.role === 'system';
  const isAssistant = message.role === 'assistant';
  const hasContent = message.content.length > 0;
  const hasArtifacts = (message.completedData?.output_files?.length ?? 0) > 0;
  const hasAssistantResult = hasContent || hasArtifacts;
  const thinkingChipActive =
    !!message.isThinking ||
    (!!message.thinkingContent && message.isStreaming && !hasAssistantResult);
  const showSpinner = isAssistant && message.isStreaming && !hasContent;
  const outputFiles = message.completedData?.output_files ?? [];
  const userContentText = message.content ?? '';
  const shouldCollapseUserContent = !isAssistant && userContentText.length > USER_BUBBLE_COLLAPSE_CHARS;
  const collapsedUserContent = shouldCollapseUserContent && !isUserContentExpanded
    ? `${userContentText.slice(0, USER_BUBBLE_COLLAPSE_CHARS)}…`
    : userContentText;
  const bubbleToggleLabel = isUserContentExpanded ? t('chat.collapse') : t('chat.expandAll');
  const isInvitation = !isAssistant
    && message.contentType === 'invitation'
    && typeof message.metadata?.invitation_id === 'string';
  const systemEvent = typeof message.metadata?.system_event === 'string'
    ? message.metadata.system_event
    : '';
  const isPlainSystemNotice = systemEvent === 'member_left' || systemEvent === 'member_reinvited';
  /* 통합 에이전트(agent 모드) 의 어시스턴트 메시지는 wallpaper 활성 여부와
   * 무관하게 항상 글래스 카드를 두른다.  카드의 border + inset highlight +
   * box-shadow 덕분에 wallpaper 가 없는 plain 한 채팅창 배경 위에서도
   * 버블이 명확히 구분되어, 라이브 에이전트(`LiveAgentPage` 의
   * `AssistantCard`) 와 동일한 가독성을 얻는다. */
  const useAssistantWallpaperCard = isAssistant && activeMode === 'agent';
  const hasAssistantCardBody = hasContent
    || outputFiles.length > 0
    || !!message.error
    || !!message.completedData;

  if (isSystem) {
    return (
      <SystemRow data-message-id={message.id}>
        <SystemStack>
          <SystemBubble $plain={isPlainSystemNotice}>{message.content}</SystemBubble>
          {systemEvent === 'member_left' && showReinviteAction && onReinviteMember && (
            <SystemActionButton
              type="button"
              onClick={() => onReinviteMember(message)}
              disabled={reinviting}
            >
              {reinviting && <Loader size={14} />}
              {t('chat.reinvite')}
            </SystemActionButton>
          )}
        </SystemStack>
      </SystemRow>
    );
  }

  const imageFiles = React.useMemo(
    () => outputFiles.filter((f) => f.kind === 'image'),
    [outputFiles],
  );
  const videoFiles = React.useMemo(
    () => outputFiles.filter((f) => f.kind !== 'image' && isVideoOutputFile(f)),
    [outputFiles],
  );
  const audioFiles = React.useMemo(
    () => outputFiles.filter((f) => isAudioOutputFile(f)),
    [outputFiles],
  );
  const otherOutputFiles = React.useMemo(
    () => outputFiles.filter((f) => f.kind !== 'image' && !isVideoOutputFile(f) && !isAudioOutputFile(f)),
    [outputFiles],
  );
  const lightboxUrls = React.useMemo(
    () => imageFiles
      .map((f) => previewUrls[f.id])
      .filter((u): u is string => isRenderablePreviewUrl(u)),
    [imageFiles, previewUrls],
  );
  React.useEffect(() => {
    if (lightboxIdx === null) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxIdx(null);
      if (e.key === 'ArrowRight') setLightboxIdx((i) => (i !== null && i < lightboxUrls.length - 1 ? i + 1 : i));
      if (e.key === 'ArrowLeft') setLightboxIdx((i) => (i !== null && i > 0 ? i - 1 : i));
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxIdx, lightboxUrls.length]);
  const sessionId = message.completedData?.session_id;
  const userId = message.completedData?.user_id;
  const messageId = message.completedData?.message_id;

  React.useEffect(() => {
    previewUrlsRef.current = previewUrls;
  }, [previewUrls]);

  React.useEffect(() => {
    attachmentPreviewUrlsRef.current = attachmentPreviewUrls;
  }, [attachmentPreviewUrls]);

  React.useEffect(() => {
    latestUserAttachmentsRef.current = message.attachments ?? [];
  }, [message.attachments]);

  React.useEffect(() => {
    (message.attachments ?? []).forEach((attachment) => {
      if (isRenderablePreviewUrl(attachmentPreviewUrls[attachment.id])) {
        releaseUserPreviewUrl(attachment.previewUrl);
      }
    });
  }, [message.attachments, attachmentPreviewUrls, releaseUserPreviewUrl]);

  React.useEffect(() => {
    return () => {
      Object.values(previewUrlsRef.current).forEach((url) => {
        if (isRenderablePreviewUrl(url)) URL.revokeObjectURL(url);
      });
      Object.values(attachmentPreviewUrlsRef.current).forEach((url) => {
        if (isRenderablePreviewUrl(url)) URL.revokeObjectURL(url);
      });
      latestUserAttachmentsRef.current.forEach((attachment) => {
        releaseUserPreviewUrl(attachment.previewUrl);
      });
    };
  }, [releaseUserPreviewUrl]);

  React.useEffect(() => {
    setIsUserContentExpanded(false);
    setOpeningPreviewId(null);
  }, [message.id]);

  React.useEffect(() => {
    setAttachmentPreviewStatus({});
  }, [message.id]);

  React.useEffect(() => {
    const imageAttachments = (message.attachments ?? [])
      .filter((a) => a.type.startsWith('image/') && !!a.remoteUrl);
    imageAttachments.forEach((a) => {
      if (attachmentPreviewUrls[a.id] || attachmentPreviewLoading[a.id]) return;
      setAttachmentPreviewLoading((prev) => ({ ...prev, [a.id]: true }));
      authFetch(a.remoteUrl!)
        .then(async (res) => {
          if (res.status === 413) throw new Error(PREVIEW_TOO_LARGE_SENTINEL);
          if (res.status === 404) throw new Error(PREVIEW_DELETED_SENTINEL);
          if (!res.ok) throw new Error(PREVIEW_FAILED_SENTINEL);
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          setAttachmentPreviewUrls((prev) => ({ ...prev, [a.id]: url }));
        })
        .catch((error: unknown) => {
          setAttachmentPreviewUrls((prev) => ({
            ...prev,
            [a.id]: resolvePreviewFailureSentinel(error),
          }));
        })
        .finally(() => {
          setAttachmentPreviewLoading((prev) => {
            const next = { ...prev };
            delete next[a.id];
            return next;
          });
        });
    });
  }, [message.attachments, attachmentPreviewUrls, attachmentPreviewLoading]);

  React.useEffect(() => {
    const previewableMediaFiles = outputFiles.filter((f) => f.kind === 'image' || isVideoOutputFile(f) || isAudioOutputFile(f));
    previewableMediaFiles.forEach((f) => {
      if (previewUrls[f.id] || previewLoading[f.id]) return;
      setPreviewLoading((prev) => ({ ...prev, [f.id]: true }));

      const loadPreview = async (): Promise<string> => {
        if (f.file_id) {
          const signed = await getFileSignedUrl(f.file_id, 'serve');
          if (signed.url) return signed.url;
          const res = await authFetch(getFileServeUrl(f.file_id));
          if (res.status === 413) throw new Error(PREVIEW_TOO_LARGE_SENTINEL);
          if (res.status === 404) throw new Error(PREVIEW_DELETED_SENTINEL);
          if (!res.ok) throw new Error(PREVIEW_FAILED_SENTINEL);
          const blob = await res.blob();
          return URL.createObjectURL(blob);
        }
        if (!sessionId || !userId || !messageId) throw new Error('no session');
        const blob = await previewAgentFile({ userId, sessionId, messageId, fileId: f.id });
        return URL.createObjectURL(blob);
      };

      loadPreview()
        .then((url) => {
          setPreviewUrls((prev) => ({ ...prev, [f.id]: url }));
        })
        .catch((error: unknown) => {
          setPreviewUrls((prev) => ({
            ...prev,
            [f.id]: resolvePreviewFailureSentinel(error),
          }));
        })
        .finally(() => {
          setPreviewLoading((prev) => {
            const next = { ...prev };
            delete next[f.id];
            return next;
          });
        });
    });
  }, [outputFiles, previewUrls, previewLoading, sessionId, userId, messageId]);

  /* 그룹채팅에서 다른 유저 메시지인지 판별 */
  const isGroupChat = !!sharedGroup;
  const isOtherUserInGroup = isGroupChat && !isAssistant && !!message.senderId && message.senderId !== currentUserId;

  /* 그룹 멤버 프로필 조회 */
  const senderProfile = React.useMemo(() => {
    if (!sharedGroup || !message.senderId) return null;
    return sharedGroup.members.find((m) => m.user_id === message.senderId) ?? null;
  }, [sharedGroup, message.senderId]);

  const senderDisplayName = message.senderName || senderProfile?.name || senderProfile?.email || t('chat.teamMember');
  const senderInitial = senderDisplayName.charAt(0).toUpperCase();
  const senderPicture = senderProfile?.picture;
  const senderAvatarKey = message.senderId
    ? `sender:${message.id}:${message.senderId}:${senderPicture || 'none'}`
    : `sender:${message.id}:anonymous:${senderPicture || 'none'}`;
  const replyAvatarKey = replyTo
    ? `reply:${message.id}:${replyTo.messageId}:${replyTo.senderPicture || 'none'}`
    : '';

  /* Phase 11-5 / PR-FE-1: tenant sub-feature gates, namespaced by the
   * active :data:`ChatModeContext` so the same MessageBubble reuses
   * cleanly across feature mounts (unified agent → ``mode.agent.*``;
   * doc-agent → ``mode.doc-agent.*``).
   *  - ``reactions``      → show/hide reaction bar + emoji picker.
   *  - ``outputDownload`` → show/hide every file/attachment/media
   *                         download affordance. */
  const reactionsEnabled = useChatFeature('reactions');
  const outputDownloadEnabled = useChatFeature('outputDownload');
  const reactions = reactionsEnabled ? (message.reactions || []) : [];

  const markGroupMemberAvatarError = (avatarKey: string) => {
    setGroupMemberAvatarErrorIds((prev) => {
      if (prev[avatarKey]) return prev;
      return { ...prev, [avatarKey]: true };
    });
  };

  const markReplyAvatarError = (avatarKey: string) => {
    setReplyAvatarErrorIds((prev) => {
      if (prev[avatarKey]) return prev;
      return { ...prev, [avatarKey]: true };
    });
  };

  const renderUserAttachment = React.useCallback((a: NonNullable<ChatMessage['attachments']>[number]) => {
    const isImage = a.type.startsWith('image/');
    const loadingKey = `att:${a.id}`;
    const info = getBubbleFileType(a.name, a.type);
    if (isImage) {
      const localPreviewSrc = isRenderablePreviewUrl(a.previewUrl) ? a.previewUrl : undefined;
      const remotePreviewSrc = isRenderablePreviewUrl(attachmentPreviewUrls[a.id])
        ? attachmentPreviewUrls[a.id]
        : undefined;
      const src = remotePreviewSrc ?? localPreviewSrc ?? '';
      const previewState = attachmentPreviewStatus[a.id];
      const hasImagePreview = !!src;
      const hasStateForSrc = hasImagePreview && !!previewState && previewState.src === src;
      const currentPreviewStatus = hasStateForSrc ? previewState?.status : undefined;
      const isRemoteFailed = isPreviewFailure(attachmentPreviewUrls[a.id]);
      const isDeletedPreview = attachmentPreviewUrls[a.id] === PREVIEW_DELETED_SENTINEL;
      const isThumbFailed = currentPreviewStatus === 'failed';
      const isRemoteStillPending = !!a.remoteUrl && !remotePreviewSrc && !isRemoteFailed;
      const isImageFailed = isDeletedPreview || (!hasImagePreview && isRemoteFailed) || (isThumbFailed && !isRemoteStillPending);
      const isImageLoading = !isImageFailed && (
        isRemoteStillPending
        || !!attachmentPreviewLoading[a.id]
        || (hasImagePreview && (!hasStateForSrc || currentPreviewStatus === 'loading'))
      );
      const isImageReady = currentPreviewStatus === 'loaded';
      const failureMessage = isThumbFailed ? t('chat.previewFailed') : getPreviewFailureMessage(attachmentPreviewUrls[a.id], t);

      return (
        <BubbleFileCard key={a.id}>
          <BubbleImageThumbFrame>
            {hasImagePreview && (
              <BubbleImageThumb
                src={src}
                alt={a.name}
                $visible={isImageReady}
                onLoad={() => {
                  setAttachmentPreviewStatus((prev) => ({
                    ...prev,
                    [a.id]: { src, status: 'loaded' },
                  }));
                }}
                onError={() => {
                  setAttachmentPreviewStatus((prev) => ({
                    ...prev,
                    [a.id]: { src, status: 'failed' },
                  }));
                }}
              />
            )}
            {isImageLoading ? (
              <BubbleImageThumbSkeleton />
            ) : isImageFailed ? (
              <BubbleImageThumbPlaceholder
                $nonInteractive={isDeletedPreview}
                onClick={isDeletedPreview ? handleBlockedPreviewClick : undefined}
              >
                {failureMessage}
              </BubbleImageThumbPlaceholder>
            ) : null}
          </BubbleImageThumbFrame>
          <BubbleFileInfo>
            <BubbleFileName title={a.name}>{a.name}</BubbleFileName>
            <BubbleFileSize>{formatFileSize(a.size)}</BubbleFileSize>
          </BubbleFileInfo>
          {outputDownloadEnabled && (
            <BubbleDownloadBtn
              onClick={(e) => {
                e.stopPropagation();
                void handleAttachmentDownload(a);
              }}
              disabled={downloadingId === loadingKey}
              title={t('tooltip.download')}
            >
              {downloadingId === loadingKey ? <Loader size={14} /> : <Download size={14} />}
            </BubbleDownloadBtn>
          )}
        </BubbleFileCard>
      );
    }
    return (
      <BubbleFileCard key={a.id}>
        <BubbleIconBadge $bg={info.bg} $color={info.color}>
          {info.icon}
          <BubbleExtLabel $color={info.color}>{info.ext}</BubbleExtLabel>
        </BubbleIconBadge>
        <BubbleFileInfo>
          <BubbleFileName title={a.name}>{a.name}</BubbleFileName>
          <BubbleFileSize>{formatFileSize(a.size)}</BubbleFileSize>
        </BubbleFileInfo>
        {outputDownloadEnabled && (
          <BubbleDownloadBtn
            onClick={(e) => {
              e.stopPropagation();
              void handleAttachmentDownload(a);
            }}
            disabled={downloadingId === loadingKey}
            title={t('tooltip.download')}
          >
            {downloadingId === loadingKey ? <Loader size={14} /> : <Download size={14} />}
          </BubbleDownloadBtn>
        )}
      </BubbleFileCard>
    );
  }, [
    downloadingId,
    handleAttachmentDownload,
    attachmentPreviewUrls,
    attachmentPreviewLoading,
    attachmentPreviewStatus,
    handleBlockedPreviewClick,
    outputDownloadEnabled,
  ]);

  const getReactionTooltip = React.useCallback((userIds: string[]): string => {
    if (!sharedGroup) return '';
    return userIds.map((uid) => {
      if (uid === currentUserId) return t('chat.me');
      const m = sharedGroup.members.find((mem) => mem.user_id === uid);
      return m?.name || m?.email || t('chat.teamMember');
    }).join(', ');
  }, [sharedGroup, currentUserId]);

  /* 그룹채팅 액션바 바깥 클릭 */
  React.useEffect(() => {
    if (!showGroupActions) return;
    const handler = (e: MouseEvent) => {
      if (showEmojiPicker) return;
      if (groupWrapRef.current && !groupWrapRef.current.contains(e.target as Node)) {
        setShowGroupActions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showGroupActions, showEmojiPicker]);

  const handleGroupBubbleClick = React.useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (showEmojiPicker) return;
    setShowGroupActions((v) => !v);
  }, [showEmojiPicker]);

  const handleGroupEmojiClick = React.useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    emojiAnchorEl.current = e.currentTarget as HTMLElement;
    setShowEmojiPicker(true);
  }, []);

  const handleGroupEmojiSelect = React.useCallback((emoji: string) => {
    if (onToggleReaction && conversationId && currentUserId) {
      onToggleReaction(conversationId, message.id, emoji, currentUserId);
    }
    setShowEmojiPicker(false);
    setShowGroupActions(false);
  }, [onToggleReaction, conversationId, message.id, currentUserId]);

  const handleGroupEmojiClose = React.useCallback(() => {
    setShowEmojiPicker(false);
  }, []);

  const handleExistingReactionClick = React.useCallback((emoji: string) => {
    if (onToggleReaction && conversationId && currentUserId) {
      onToggleReaction(conversationId, message.id, emoji, currentUserId);
    }
  }, [onToggleReaction, conversationId, message.id, currentUserId]);

  const getEmojiAnchorRect = React.useCallback((): DOMRect | null => {
    return emojiAnchorEl.current?.getBoundingClientRect() ?? null;
  }, []);

  const formatTime = (ts: number) =>
    new Date(ts).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });

  const assistantBody = (
    <>
      {/* Content */}
      {hasContent && (
        <AssistantContent>
          <React.Suspense fallback={<AssistantPlainTextFallback>{message.content}</AssistantPlainTextFallback>}>
            <MarkdownRenderer
              content={message.content}
              isStreaming={message.isStreaming}
            />
          </React.Suspense>
        </AssistantContent>
      )}

      {/* Generative UI Widgets */}
      {message.widgets && message.widgets.length > 0 && (
        <React.Suspense fallback={null}>
          {message.widgets.map((w) => (
            <WidgetRenderer key={w.widgetId} widget={w} />
          ))}
        </React.Suspense>
      )}

      {/* Output files */}
      {otherOutputFiles.length > 0 && (
        <OutputFileList>
          {otherOutputFiles.map((f) => {
            const info = getOutputFileType(f.name, f.mime_type);
            /* "열기": 렌더 가능한 확장자에서만 노출(이미지/비디오/오디오는 별도
             * 분기라 여기 없음 → 자동 제외). 통합 에이전트(activeMode==='agent')는
             * 기본 setPreviewFile 로 좌측 미리보기를, 견적 등 자체 좌측 렌더 surface
             * 는 onOpenOutputFile 콜백으로 위임. 그 외 모드는 좌측 렌더가 없어 숨김. */
            const canOpen =
              canPreviewOutputFile(f) && (!!onOpenOutputFile || activeMode === 'agent');
            return (
              <OutputFileCard key={f.id}>
                <BubbleIconBadge $bg={info.bg} $color={info.color}>
                  {info.icon}
                  <BubbleExtLabel $color={info.color}>{info.ext}</BubbleExtLabel>
                </BubbleIconBadge>
                <OutputFileMeta>
                  <BubbleFileName title={f.name}>{f.name}</BubbleFileName>
                  <BubbleFileSize>{formatFileSize(f.size)}</BubbleFileSize>
                  <OutputFileHint>{t('chat.savedToStorage')}</OutputFileHint>
                </OutputFileMeta>
                {(canOpen || outputDownloadEnabled) && (
                  <OutputFileActions>
                    {canOpen && (
                      <OutputFileAction
                        type="button"
                        onClick={() =>
                          onOpenOutputFile ? onOpenOutputFile(f) : void handlePreview(f)
                        }
                      >
                        <Eye size={14} />
                        {t('chat.open', { defaultValue: '열기' })}
                      </OutputFileAction>
                    )}
                    {outputDownloadEnabled && (
                      <OutputFileAction
                        type="button"
                        onClick={() => handleDownload(f)}
                        disabled={downloadingId === f.id}
                      >
                        <Download size={14} />
                        {downloadingId === f.id ? t('chat.receiving') : t('tooltip.download')}
                      </OutputFileAction>
                    )}
                  </OutputFileActions>
                )}
              </OutputFileCard>
            );
          })}
        </OutputFileList>
      )}

      {imageFiles.length > 0 && (
        <OutputImageList>
          {imageFiles.map((f) => {
            const previewUrl = previewUrls[f.id];
            const hasPreview = isRenderablePreviewUrl(previewUrl);
            const isPreviewFailed = isPreviewFailure(previewUrl);
            const isDeletedPreview = previewUrl === PREVIEW_DELETED_SENTINEL;
            const isPreviewLoading = !!previewLoading[f.id] || (!hasPreview && !isPreviewFailed);
            const lbIdx = lightboxUrls.indexOf(previewUrl as string);
            return (
              <OutputImageBlock key={f.id}>
                <OutputImageFrame>
                  {hasPreview ? (
                    <ImageClickable onClick={() => lbIdx !== -1 && setLightboxIdx(lbIdx)}>
                      <OutputImagePreview src={previewUrl} alt={f.name} />
                    </ImageClickable>
                  ) : isPreviewLoading ? (
                    <OutputImageSkeleton />
                  ) : (
                    <OutputImagePlaceholder
                      $nonInteractive={isDeletedPreview}
                      onClick={isDeletedPreview ? handleBlockedPreviewClick : undefined}
                    >
                      {getPreviewFailureMessage(previewUrl, t)}
                    </OutputImagePlaceholder>
                  )}
                  {!isDeletedPreview && outputDownloadEnabled && (
                    <OutputImageOverlay
                      onClick={() => handleDownload(f)}
                      disabled={downloadingId === f.id}
                    >
                      <Tooltip label={downloadingId === f.id ? t('chat.downloading') : t('tooltip.download')}>
                        <span>
                          {downloadingId === f.id ? (
                            <OverlaySpin><Loader size={14} /></OverlaySpin>
                          ) : (
                            <Download size={14} />
                          )}
                        </span>
                      </Tooltip>
                    </OutputImageOverlay>
                  )}
                </OutputImageFrame>
                <OutputFileMeta>
                  <BubbleFileName title={f.name}>{f.name}</BubbleFileName>
                  <BubbleFileSize>{formatFileSize(f.size)}</BubbleFileSize>
                  <OutputFileHint>{t('chat.savedToStorage')}</OutputFileHint>
                </OutputFileMeta>
              </OutputImageBlock>
            );
          })}
        </OutputImageList>
      )}

      {videoFiles.length > 0 && (
        <OutputImageList>
          {videoFiles.map((f) => {
            const previewUrl = previewUrls[f.id];
            const hasPreview = isRenderablePreviewUrl(previewUrl);
            const isPreviewFailed = isPreviewFailure(previewUrl);
            const isDeletedPreview = previewUrl === PREVIEW_DELETED_SENTINEL;
            const isPreviewLoading = !!previewLoading[f.id] || (!hasPreview && !isPreviewFailed);

            return (
              <OutputImageBlock key={f.id}>
                <OutputVideoFrame>
                  {hasPreview ? (
                    <OutputVideoPreviewTrigger type="button" onClick={() => void handlePreview(f)}>
                      <OutputVideoPreview
                        src={previewUrl}
                        muted
                        playsInline
                        preload="metadata"
                      />
                      <OutputVideoPlayBadge>
                        <Play size={24} fill="currentColor" />
                      </OutputVideoPlayBadge>
                    </OutputVideoPreviewTrigger>
                  ) : isPreviewLoading ? (
                    <OutputImageSkeleton />
                  ) : (
                    <OutputImagePlaceholder
                      $nonInteractive={isDeletedPreview}
                      onClick={isDeletedPreview ? handleBlockedPreviewClick : undefined}
                    >
                      {getPreviewFailureMessage(previewUrl, t)}
                    </OutputImagePlaceholder>
                  )}
                  {!isDeletedPreview && outputDownloadEnabled && (
                    <OutputMediaActionBar>
                      <Tooltip label={downloadingId === f.id ? t('chat.downloading') : t('tooltip.download')}>
                        <span>
                          <OutputMediaActionButton
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              void handleDownload(f);
                            }}
                            disabled={downloadingId === f.id}
                          >
                            {downloadingId === f.id ? (
                              <OverlaySpin><Loader size={14} /></OverlaySpin>
                            ) : (
                              <Download size={14} />
                            )}
                          </OutputMediaActionButton>
                        </span>
                      </Tooltip>
                    </OutputMediaActionBar>
                  )}
                </OutputVideoFrame>
                <OutputFileMeta>
                  <BubbleFileName title={f.name}>{f.name}</BubbleFileName>
                  <BubbleFileSize>{formatFileSize(f.size)}</BubbleFileSize>
                  <OutputFileHint>{t('chat.savedToStorage')}</OutputFileHint>
                </OutputFileMeta>
              </OutputImageBlock>
            );
          })}
        </OutputImageList>
      )}

      {audioFiles.length > 0 && (
        <OutputFileList>
          {audioFiles.map((f) => {
            const audioSrc = previewUrls[f.id];
            const hasAudioSrc = isRenderablePreviewUrl(audioSrc);
            return (
              <OutputAudioCard key={f.id}>
                {hasAudioSrc ? (
                  <OutputAudioPlayer
                    controls
                    preload="metadata"
                    src={audioSrc}
                  />
                ) : (
                  <OutputAudioPlayer controls preload="none" />
                )}
                <OutputFileMeta>
                  <BubbleFileName title={f.name}>{f.name}</BubbleFileName>
                  <BubbleFileSize>{formatFileSize(f.size)}</BubbleFileSize>
                  <OutputFileHint>{t('chat.savedToStorage')}</OutputFileHint>
                </OutputFileMeta>
                {outputDownloadEnabled && (
                  <OutputFileActions>
                    <OutputFileAction
                      type="button"
                      onClick={() => handleDownload(f)}
                      disabled={downloadingId === f.id}
                    >
                      <Download size={14} />
                      {downloadingId === f.id ? t('chat.receiving') : t('tooltip.download')}
                    </OutputFileAction>
                  </OutputFileActions>
                )}
              </OutputAudioCard>
            );
          })}
        </OutputFileList>
      )}

      {/* Error */}
      {message.error && (
        <ErrorBanner>
          <AlertTriangle size={16} />
          {message.error}
        </ErrorBanner>
      )}

      {/* Actions */}
      {!message.isStreaming && hasContent && (
        <Actions>
          {isGroupChat && (
            <Tooltip label={t('tooltip.emojiReaction')}>
              <ActionBtn onClick={handleGroupEmojiClick}>
                <Smile size={14} />
              </ActionBtn>
            </Tooltip>
          )}
          <Tooltip label={copied ? t('chat.copied') : t('tooltip.copy')}>
            <ActionBtn onClick={handleCopy}>
              {copied ? <Check size={14} /> : <Copy size={14} />}
            </ActionBtn>
          </Tooltip>
          <Tooltip label={t('tooltip.like')}>
            <FeedbackBtn
              $active={feedback === 'up'}
              className={shakingFeedback === 'up' ? 'shaking' : ''}
              onClick={() => handleFeedback('up')}
            >
              <ThumbsUp size={14} />
            </FeedbackBtn>
          </Tooltip>
          <Tooltip label={t('tooltip.dislike')}>
            <FeedbackBtn
              $active={feedback === 'down'}
              className={shakingFeedback === 'down' ? 'shaking' : ''}
              onClick={() => handleFeedback('down')}
            >
              <ThumbsDown size={14} />
            </FeedbackBtn>
          </Tooltip>
          {onRegenerate && (
            <RegenerateGroup ref={regenerateMenuRef}>
              <Tooltip label={t('tooltip.regenerate')}>
                <RegenerateBtn onClick={handleRegenerate}>
                  <RefreshCw size={14} />
                </RegenerateBtn>
              </Tooltip>
              <RegenerateModeBtn onClick={() => setRegenerateMenuOpen((v) => !v)}>
                <span>{t(`reboMode.${regenerateMode}Label`)}</span>
                <ChevronDown size={10} />
              </RegenerateModeBtn>
              {regenerateMenuOpen && (
                <RegenerateModePopup>
                  {(['auto', 'fast', 'plan', 'skill'] as const).map((m) => (
                    <RegenerateModeOption
                      key={m}
                      $active={regenerateMode === m}
                      onClick={() => {
                        setRegenerateMode(m);
                        setRegenerateMenuOpen(false);
                      }}
                    >
                      <span>{t(`reboMode.${m}Label`)}</span>
                      {regenerateMode === m && <Check size={12} />}
                    </RegenerateModeOption>
                  ))}
                </RegenerateModePopup>
              )}
            </RegenerateGroup>
          )}
        </Actions>
      )}
    </>
  );

  /* ---- 그룹채팅: 다른 유저의 메시지 (왼쪽 정렬) ---- */
  if (isOtherUserInGroup) {
    return (
      <GroupRow data-message-id={message.id}>
        <GroupAvatarCol>
          <GroupMemberAvatar>
            {senderPicture && !groupMemberAvatarErrorIds[senderAvatarKey]
              ? (
                <GroupMemberPicture
                  src={senderPicture}
                  alt={senderDisplayName}
                  onError={() => markGroupMemberAvatarError(senderAvatarKey)}
                />
              )
              : senderInitial}
          </GroupMemberAvatar>
        </GroupAvatarCol>
        <GroupBubbleWrap ref={groupWrapRef}>
          <GroupSenderRow>
            <GroupSenderName>{senderDisplayName}</GroupSenderName>
            <GroupTimestamp>{formatTime(message.createdAt)}</GroupTimestamp>
          </GroupSenderRow>

          <GroupBubble
            $hasCollapsedToggle={shouldCollapseUserContent}
            onClick={handleGroupBubbleClick}
          >
            {isInvitation ? (
              <DMInvitationCard
                invitationId={message.metadata?.invitation_id as string}
                invitationStatus={(message.metadata?.invitation_status as string) || 'pending'}
                teamName={(message.metadata?.team_name as string) || ''}
                inviterName={message.metadata?.inviter_name as string | undefined}
                isInvitee={message.senderId !== currentUserId}
              />
            ) : (
              <>
                <GroupBubbleContent>
                  {renderMentionChips(collapsedUserContent, sharedGroup)}
                </GroupBubbleContent>
                {shouldCollapseUserContent && (
                  <UserContentToggle
                    $align="left"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsUserContentExpanded((v) => !v);
                    }}
                  >
                    {bubbleToggleLabel}
                  </UserContentToggle>
                )}

                {message.attachments && message.attachments.length > 0 && (
                  <AttachmentList>
                    {message.attachments.map((a) => renderUserAttachment(a))}
                  </AttachmentList>
                )}
              </>
            )}
          </GroupBubble>

          {reactions.length > 0 && (
            <ReactionBar>
              {reactions.map((r) => (
                <Tooltip key={r.emoji} label={getReactionTooltip(r.userIds)} align="start">
                  <ReactionBadge
                    $active={!!currentUserId && r.userIds.includes(currentUserId)}
                    onClick={() => handleExistingReactionClick(r.emoji)}
                  >
                    <span>{r.emoji}</span>
                    {r.userIds.length > 1 && <ReactionCount>{r.userIds.length}</ReactionCount>}
                  </ReactionBadge>
                </Tooltip>
              ))}
              <ReactionAddBtn onClick={handleGroupEmojiClick} title={t('chat.addEmoji')}>
                <Plus size={12} />
              </ReactionAddBtn>
            </ReactionBar>
          )}

          {showGroupActions && (
            <GroupActionsRow>
              <Tooltip label={t('tooltip.emojiReaction')}>
                <ActionBtn onClick={handleGroupEmojiClick}>
                  <Smile size={14} />
                </ActionBtn>
              </Tooltip>
              <Tooltip label={copied ? t('chat.copied') : t('tooltip.copy')}>
                <ActionBtn onClick={handleCopy}>
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                </ActionBtn>
              </Tooltip>
            </GroupActionsRow>
          )}

          {showEmojiPicker && (
            <EmojiPicker
              onSelect={handleGroupEmojiSelect}
              onClose={handleGroupEmojiClose}
              anchorRect={getEmojiAnchorRect()}
              alignRight={false}
            />
          )}
        </GroupBubbleWrap>
      </GroupRow>
    );
  }

  /* ---- 그룹채팅: 내 user 메시지 (오른쪽 정렬 — 기본 + 이모지 지원) ---- */
  const showGroupReactionsForMe = isGroupChat && !isAssistant;

  const truncateContent = (text: string, max = 40) =>
    text.length > max ? text.slice(0, max) + '…' : text;

  return (
    <>
      {/* 그룹채팅 에이전트 답변: 어떤 질문에 대한 답변인지 표시 */}
          {isGroupChat && isAssistant && replyTo && (
        <ReplyRow onClick={() => onScrollToMessage?.(replyTo.messageId)}>
          <ReplyConnector />
          <ReplyChip>
                {replyAvatarKey && replyTo.senderPicture && !replyAvatarErrorIds[replyAvatarKey]
                  ? (
                    <ReplyMiniAvatar
                      src={replyTo.senderPicture}
                      alt={replyTo.senderName}
                      onError={() => markReplyAvatarError(replyAvatarKey)}
                    />
                  )
              : <ReplyMiniAvatarFallback>{replyTo.senderName.charAt(0).toUpperCase()}</ReplyMiniAvatarFallback>}
            <ReplyName>@{replyTo.senderName}</ReplyName>
            <ReplyContent>{truncateContent(replyTo.content)}</ReplyContent>
          </ReplyChip>
        </ReplyRow>
      )}

      <Row $role={message.role} $group={isGroupChat} $narrow={narrowColumn} data-message-id={message.id}>
        {/* Avatar — 어시스턴트만 표시 */}
        {isAssistant && (
          <AvatarCol>
            <Avatar role="assistant" />
          </AvatarCol>
        )}

        {/* ---- User ---- */}
        {!isAssistant && (
          <UserSection>
            <UserBubble
              $hasCollapsedToggle={shouldCollapseUserContent}
              onClick={() => {
                setShowUserActions((v) => !v);
                if (isGroupChat) setShowGroupActions((v) => !v);
              }}
            >
              {isInvitation ? (
                <DMInvitationCard
                  invitationId={message.metadata?.invitation_id as string}
                  invitationStatus={(message.metadata?.invitation_status as string) || 'pending'}
                  teamName={(message.metadata?.team_name as string) || ''}
                  inviterName={message.metadata?.inviter_name as string | undefined}
                  isInvitee={message.senderId !== currentUserId}
                />
              ) : (
                <>
                  <UserContent>
                    {renderMentionChips(collapsedUserContent, sharedGroup)}
                  </UserContent>
                  {shouldCollapseUserContent && (
                    <UserContentToggle
                      $align="right"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsUserContentExpanded((v) => !v);
                      }}
                    >
                      {bubbleToggleLabel}
                    </UserContentToggle>
                  )}

                  {/* File attachments */}
                  {message.attachments && message.attachments.length > 0 && (
                    <AttachmentList>
                      {message.attachments.map((a) => renderUserAttachment(a))}
                    </AttachmentList>
                  )}
                </>
              )}
          </UserBubble>

          {/* 그룹채팅: 내 메시지 보낸 시간 */}
          {isGroupChat && (
            <GroupTimestamp style={{ alignSelf: 'flex-end', marginTop: 2 }}>
              {formatTime(message.createdAt)}
            </GroupTimestamp>
          )}

          {showGroupReactionsForMe && reactions.length > 0 && (
            <ReactionBar style={{ justifyContent: 'flex-end' }}>
              {reactions.map((r) => (
                <Tooltip key={r.emoji} label={getReactionTooltip(r.userIds)} align="end">
                  <ReactionBadge
                    $active={!!currentUserId && r.userIds.includes(currentUserId)}
                    onClick={() => handleExistingReactionClick(r.emoji)}
                  >
                    <span>{r.emoji}</span>
                    {r.userIds.length > 1 && <ReactionCount>{r.userIds.length}</ReactionCount>}
                  </ReactionBadge>
                </Tooltip>
              ))}
              <ReactionAddBtn onClick={handleGroupEmojiClick} title={t('chat.addEmoji')}>
                <Plus size={12} />
              </ReactionAddBtn>
            </ReactionBar>
          )}

          {showUserActions && (
            <UserActionsRow>
              {isGroupChat && (
                <Tooltip label={t('tooltip.emojiReaction')}>
                  <ActionBtn onClick={handleGroupEmojiClick}>
                    <Smile size={14} />
                  </ActionBtn>
                </Tooltip>
              )}
              <Tooltip label={copied ? t('chat.copied') : t('tooltip.copy')}>
                <ActionBtn onClick={handleCopy}>
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                </ActionBtn>
              </Tooltip>
            </UserActionsRow>
          )}

          {showEmojiPicker && (
            <EmojiPicker
              onSelect={handleGroupEmojiSelect}
              onClose={handleGroupEmojiClose}
              anchorRect={getEmojiAnchorRect()}
              alignRight={true}
            />
          )}
        </UserSection>
      )}

      {/* ---- Assistant ---- */}
      {isAssistant && (
        <Content>
          {/* 그룹채팅: 에이전트 이름 + 시간 */}
          {isGroupChat && (
            <GroupSenderRow>
              <GroupSenderName>askyour.ai</GroupSenderName>
              <GroupTimestamp>{formatTime(message.createdAt)}</GroupTimestamp>
            </GroupSenderRow>
          )}

          {useAssistantWallpaperCard ? (
            <AssistantWallpaperCard>
              <ChipsRow>
                <StreamProgress events={message.streamEvents} isStreaming={message.isStreaming} hasContent={hasAssistantResult} />
                {message.thinkingContent && (
                  <ThinkingBlock
                    content={message.thinkingContent}
                    isThinking={thinkingChipActive}
                    isStreaming={message.isStreaming}
                    hasContent={hasAssistantResult}
                  />
                )}
              </ChipsRow>
              {showSpinner && <DotsSpinner />}
              {assistantBody}
            </AssistantWallpaperCard>
          ) : (
            <>
              <ChipsRow>
                <StreamProgress events={message.streamEvents} isStreaming={message.isStreaming} hasContent={hasAssistantResult} />
                {message.thinkingContent && (
                  <ThinkingBlock
                    content={message.thinkingContent}
                    isThinking={thinkingChipActive}
                    isStreaming={message.isStreaming}
                    hasContent={hasAssistantResult}
                  />
                )}
              </ChipsRow>
              {showSpinner && <DotsSpinner />}
              {assistantBody}
            </>
          )}

          {/* Lightbox */}
          {lightboxIdx !== null && lightboxUrls[lightboxIdx] && (
            <LightboxOverlay onClick={() => setLightboxIdx(null)}>
              <LightboxImg
                src={lightboxUrls[lightboxIdx]}
                alt={t('chat.viewFullImage')}
                onClick={(e) => e.stopPropagation()}
              />
              <LightboxClose onClick={() => setLightboxIdx(null)}>
                <XIcon size={18} />
              </LightboxClose>
              {lightboxIdx > 0 && (
                <LightboxNav
                  $side="left"
                  onClick={(e) => { e.stopPropagation(); setLightboxIdx((i) => (i !== null ? i - 1 : i)); }}
                >
                  <ChevronLeft size={22} />
                </LightboxNav>
              )}
              {lightboxIdx < lightboxUrls.length - 1 && (
                <LightboxNav
                  $side="right"
                  onClick={(e) => { e.stopPropagation(); setLightboxIdx((i) => (i !== null ? i + 1 : i)); }}
                >
                  <ChevronRight size={22} />
                </LightboxNav>
              )}
              {lightboxUrls.length > 1 && (
                <LightboxCounter>{lightboxIdx + 1} / {lightboxUrls.length}</LightboxCounter>
              )}
            </LightboxOverlay>
          )}

          {/* Reaction bar for assistant messages in group chat */}
          {isGroupChat && reactions.length > 0 && (
            <ReactionBar>
              {reactions.map((r) => (
                <Tooltip key={r.emoji} label={getReactionTooltip(r.userIds)} align="start">
                  <ReactionBadge
                    $active={!!currentUserId && r.userIds.includes(currentUserId)}
                    onClick={() => handleExistingReactionClick(r.emoji)}
                  >
                    <span>{r.emoji}</span>
                    {r.userIds.length > 1 && <ReactionCount>{r.userIds.length}</ReactionCount>}
                  </ReactionBadge>
                </Tooltip>
              ))}
              <ReactionAddBtn onClick={handleGroupEmojiClick} title={t('chat.addEmoji')}>
                <Plus size={12} />
              </ReactionAddBtn>
            </ReactionBar>
          )}

          {showEmojiPicker && (
            <EmojiPicker
              onSelect={handleGroupEmojiSelect}
              onClose={handleGroupEmojiClose}
              anchorRect={getEmojiAnchorRect()}
              alignRight={false}
            />
          )}
        </Content>
      )}
    </Row>
    </>
  );
};

export const MessageBubble = React.memo(MessageBubbleImpl, areMessageBubblePropsEqual);
MessageBubble.displayName = 'MessageBubble';
